// Fiche personnalité canonique AYA ID — /personnalite/:slug
// Socle commun reproduisant les maquettes : hero immersif + onglets à icônes,
// Biographie riche (quote card, galerie, parcours, fonctions, engagements, vidéos, actualités),
// Engagements documentés (filtres, domaine vedette, domaines, initiatives), barre d'actions fixe.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Share2, Bookmark, BadgeCheck, CalendarDays, User, GraduationCap, Activity, Landmark,
  HandHeart, BookOpen, Camera, PlayCircle, ExternalLink, Star, ArrowRight, Heart, LayoutGrid,
  FileText, Info, MessageSquareText, Crown, Users, Globe2, Handshake, MessageCircle, Route,
} from "lucide-react";
import { toast } from "sonner";
import "./persona.css";

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { id: "bio", label: "Biographie", Icon: User }, { id: "parcours", label: "Parcours", Icon: GraduationCap },
  { id: "activite", label: "Activité", Icon: Activity }, { id: "fonctions", label: "Fonctions", Icon: Landmark },
  { id: "engagements", label: "Engagements", Icon: Heart }, { id: "oeuvres", label: "Œuvres", Icon: BookOpen },
  { id: "photos", label: "Photos", Icon: Camera }, { id: "videos", label: "Vidéos", Icon: PlayCircle },
];
const PERIODS = [["7j", 7], ["30 jours", 30], ["3 mois", 92], ["1 an", 365], ["Tout", 99999]];
const FN_ICONS = [Crown, Users, Heart, Globe2, Handshake];
const Empty = ({ label }) => <div className="pf-empty" data-testid="pf-empty">Aucun contenu documenté pour « {label} » pour le moment.</div>;
const SecHead = ({ Icon, title, sub, more, onMore }) => (
  <div className="pf2-sechead">
    {Icon && <span className="pf2-secicon"><Icon size={15} /></span>}
    <div style={{ flex: 1, minWidth: 0 }}>
      <h3>{title}</h3>
      {sub && <p>{sub}</p>}
    </div>
    {more && <button className="pf2-more" onClick={onMore}>{more} <ArrowRight size={12} /></button>}
  </div>
);
const dateFr = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

export default function PersonnalitePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("bio");
  const [bioFull, setBioFull] = useState(false);
  const [domF, setDomF] = useState("Tous");
  const [period, setPeriod] = useState(99999);
  const [srcF, setSrcF] = useState("Toutes les sources");
  const [typeF, setTypeF] = useState("Tout");

  useEffect(() => {
    const t = localStorage.getItem("civ_user_token");
    fetch(`${API}/api/personalities/${slug}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => r.json()).then((j) => (j.ok ? setP(j.personality) : setErr(true))).catch(() => setErr(true));
  }, [slug]);

  const acts = useMemo(() => {
    if (!p) return [];
    const since = Date.now() - period * 86400000;
    return (p.activities || []).filter((a) => new Date(a.date).getTime() >= since)
      .filter((a) => srcF === "Toutes les sources" || a.source === srcF)
      .filter((a) => typeF === "Tout" || a.type === typeF);
  }, [p, period, srcF, typeF]);
  const sources = useMemo(() => ["Toutes les sources", ...new Set((p?.activities || []).map((a) => a.source))], [p]);
  const types = useMemo(() => ["Tout", ...new Set((p?.activities || []).map((a) => a.type))], [p]);

  if (err) return <div className="pf-page" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="pf-notfound">Fiche introuvable.</div>;
  if (!p) return <div className="pf-page" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="pf-loading">Chargement…</div>;

  const oeuvresLabel = p.oeuvres_label || "Œuvres";
  const domains = p.domains || [];
  const initiatives = (p.initiatives || []).filter((i) => domF === "Tous" || i.domain === domF);
  const featured = p.featured_engagement && (domF === "Tous" || p.featured_engagement.domain === domF) ? p.featured_engagement : null;
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: p.name, url }); } catch { /* annulé */ } }
    else { try { await navigator.clipboard.writeText(url); toast.success("Lien AYA ID copié"); } catch { toast.error("Copie impossible"); } }
  };
  const demo = (m) => toast.info(`${m} — fonctionnalité de démonstration.`);

  const Provenance = () => (
    <div className="pf-src">📄 Provenance : {p.provenance === "editorial_demo" ? "contenu éditorial AYA (démonstration)" : p.provenance === "editorial_ai" ? "contenu éditorial AYA généré (démonstration)" : p.provenance}{p.portrait_note === "illustration" ? " · visuels : illustration" : ""}</div>
  );

  return (
    <div className="pf-page pf2-haspad" data-testid={`personnalite-page-${p.slug}`}>
      <div className="pf-top">
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="pf-back"><ArrowLeft size={20} /></button>
        <div className="t"><b>{p.short_name}</b><span>{p.title}</span></div>
        <button onClick={share} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="pf-share"><Share2 size={18} /></button>
        <button onClick={() => toast.success("Fiche enregistrée")} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="pf-save"><Bookmark size={18} /></button>
      </div>

      <div className="pf-hero" data-testid="pf-hero">
        <img className="bg" src={p.cover || p.portrait} alt={p.name} />
        <div className="ovl" />
        <div className="inner">
          {p.org_badge && <span className="pr-badge orange">👑 {p.org_badge}</span>}
          <h1>{p.name}</h1>
          <div style={{ color: "#F3F8F4", fontSize: 14, fontWeight: 700, marginTop: 3 }}>{p.title}</div>
          {p.quote && <div className="pf2-heroquote">« {p.quote} »</div>}
        </div>
      </div>
      <div className="pf2-heropills" data-testid="pf-hero-pills">
        <span className="pf2-hpill"><span className="ms-flag" style={{ width: 16, height: 11 }}><i style={{ background: "#F97316" }} /><i style={{ background: "#fff" }} /><i style={{ background: "#059669" }} /></span> {p.location || "Côte d'Ivoire"}</span>
        {p.born && <span className="pf2-hpill"><CalendarDays size={12} /> {p.born}</span>}
        {p.verified && <span className="pf2-hpill v"><BadgeCheck size={14} color="#fff" /> <span>Personnalité officielle<br />Vérifiée par AYA</span></span>}
      </div>

      <div className="pf-tabs pf2-tabs" data-testid="pf-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} className={`pf2-tab ${tab === id ? "on" : ""}`} onClick={() => setTab(id)} data-testid={`pf-tab-${id}`}>
            <Icon size={17} />
            <span>{id === "oeuvres" ? oeuvresLabel.split(" ")[0] : label}</span>
          </button>
        ))}
      </div>

      <div className="pf-body">
        {/* ═══ BIOGRAPHIE (maquette Réf 4) ═══ */}
        {tab === "bio" && (
          <>
            <div className="pf2-biogrid">
              <div className="pf-sec" style={{ margin: 0 }} data-testid="pf-bio">
                <SecHead title="Biographie" />
                <p className={`pf2-biotext ${bioFull ? "full" : ""}`}>{p.bio}</p>
                {(p.bio || "").length > 180 && (
                  <button className="pf2-more" style={{ marginTop: 6 }} data-testid="pf-bio-more" onClick={() => setBioFull(!bioFull)}>{bioFull ? "Réduire" : "Lire la suite"} <ArrowRight size={12} /></button>
                )}
              </div>
              {p.quote_card && (
                <div className="pf2-quotecard" data-testid="pf-quote-card">
                  <span className="q">“</span>
                  <p>« {p.quote_card.text} »</p>
                  <div className="sig">{p.quote_card.author}</div>
                </div>
              )}
            </div>

            {(p.photos || []).length > 1 && (
              <div className="pf-sec">
                <SecHead Icon={Camera} title="Galerie photos" more="Voir toutes les photos" onMore={() => setTab("photos")} />
                <div className="pr-row">{p.photos.slice(0, 6).map((ph) => <img key={ph} src={ph} alt="" className="pf2-gph" />)}</div>
              </div>
            )}

            {(p.parcours || []).length > 0 && (
              <div className="pf-sec">
                <SecHead Icon={Route} title="Son parcours" more="Voir le détail" onMore={() => setTab("parcours")} />
                <div className="pf2-parcoursgrid">
                  <div className="pf-tl">{p.parcours.map((e, i) => (
                    <div className="pf-tli" key={i}><span className="meta">{e.period}</span><b style={{ fontSize: 12.5 }}>{e.text}</b></div>
                  ))}</div>
                  {p.parcours_note && (
                    <div className="pf2-stepcard"><Route size={22} color="#0E8A4F" /><b>Étapes clés</b><p>{p.parcours_note}</p></div>
                  )}
                </div>
              </div>
            )}

            {(p.fonctions || []).length > 0 && (
              <div className="pf-sec">
                <SecHead Icon={Landmark} title="Fonctions et responsabilités" />
                <div className="pf2-fngrid" data-testid="pf-fonctions-tiles">
                  {p.fonctions.slice(0, 5).map((f, i) => {
                    const FIcon = FN_ICONS[i % FN_ICONS.length];
                    return <div className="pf2-fntile" key={i}><FIcon size={19} /><b>{f.label}{f.org ? ` ${f.org}` : ""}</b></div>;
                  })}
                </div>
              </div>
            )}

            {domains.length > 0 && (
              <div className="pf-sec">
                <SecHead Icon={Heart} title="Engagements et actions" more="Voir plus" onMore={() => setTab("engagements")} />
                <div className="pr-row">
                  {domains.map((d) => (
                    <div className="pf2-domcard sm" key={d.label}>
                      <img src={d.image} alt="" />
                      <b>{d.label}</b><span>{d.sub}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(p.videos || []).length > 0 && (
              <div className="pf-sec">
                <SecHead Icon={PlayCircle} title="Vidéos" more="Voir plus" onMore={() => setTab("videos")} />
                <div className="pr-row">
                  {p.videos.slice(0, 5).map((v, i) => (
                    <div className="pf2-vid" key={i}>
                      <div className="th"><img src={v.image} alt="" /><span className="pl"><PlayCircle size={22} color="#fff" /></span>{v.duration && <span className="dur">{v.duration}</span>}</div>
                      <b>{v.title}</b>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(p.news || []).length > 0 && (
              <div className="pf-sec">
                <SecHead Icon={FileText} title="Actualités récentes" more="Voir toutes les actualités" onMore={() => setTab("activite")} />
                <div className="pr-row">
                  {p.news.map((n, i) => (
                    <div className="pf2-news" key={i}>
                      <img src={n.image} alt="" />
                      <span className="d">{dateFr(n.date)}</span>
                      <b>{n.title}</b>
                      <span className="s">{n.source}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pf-sec"><Provenance />
              {p.rating && <b style={{ fontSize: 13, display: "block", marginTop: 6 }}><Star size={13} color="#F59E0B" fill="#F59E0B" style={{ verticalAlign: -2 }} /> {String(p.rating).replace(".", ",")} ({(p.reviews_count || 0).toLocaleString("fr-FR")} avis)</b>}
            </div>
          </>
        )}

        {/* ═══ ENGAGEMENTS (maquette Réf 1) ═══ */}
        {tab === "engagements" && (
          domains.length || (p.engagements || []).length ? (
            <>
              <div className="pf-sec" style={{ paddingBottom: 10 }}>
                <SecHead Icon={Heart} title="Engagements et actions" sub="Causes soutenues et initiatives documentées" />
                {domains.length > 0 && (
                  <div className="pf-filters" data-testid="pf-domain-filters">
                    {["Tous", ...domains.map((d) => d.label)].map((d) => (
                      <button key={d} className={`pf-f ${domF === d ? "on" : ""}`} onClick={() => setDomF(d)} data-testid={`pf-domain-${d}`}>{d}</button>
                    ))}
                  </div>
                )}
              </div>

              {featured && (
                <div className="pf2-featured" data-testid="pf-featured-engagement">
                  <img src={featured.image} alt="" />
                  <div className="bd">
                    <b>{featured.title}</b>
                    <p>{featured.text}</p>
                    <div className="srcrow">
                      <span className="dot" />
                      <span className="src"><b>{featured.source}</b><br />{featured.source_kind}</span>
                      <button className="pf2-srcbtn" onClick={() => demo("Consulter la source")}>Consulter la source <ExternalLink size={11} /></button>
                    </div>
                  </div>
                </div>
              )}

              {domains.length > 0 && (
                <div className="pf-sec">
                  <SecHead Icon={LayoutGrid} title="Domaines d'engagement" more="Voir tout" onMore={() => setDomF("Tous")} />
                  <div className="pf2-domgrid" data-testid="pf-domains-grid">
                    {(domF === "Tous" ? domains : domains.filter((d) => d.label === domF)).map((d) => (
                      <div className="pf2-domcard" key={d.label}>
                        <img src={d.image} alt="" />
                        <b>{d.label}</b><span>{d.sub}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {initiatives.length > 0 && (
                <div className="pf-sec">
                  <SecHead Icon={FileText} title="Initiatives documentées" more="Voir tout" onMore={() => setDomF("Tous")} />
                  <div className="pf2-inits" data-testid="pf-initiatives">
                    {initiatives.map((it, i) => (
                      <div className="pf2-init" key={i}>
                        <span className="rail"><span className="dot" />{i < initiatives.length - 1 && <i />}</span>
                        <img src={it.image} alt="" />
                        <div className="bd">
                          <b>{it.title}</b>
                          <p>{it.text}</p>
                          <span className="src">🟢 {it.source} · {it.source_kind}</span>
                        </div>
                        <ExternalLink size={14} color="#0E8A4F" style={{ flexShrink: 0, marginTop: 4 }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!domains.length && (p.engagements || []).map((e, i) => (
                <div className="pf-sec" key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><b style={{ fontSize: 13 }}>{e.label}</b><span className="pr-chip" style={{ flexShrink: 0 }}>{e.status}</span></div>
              ))}

              <div className="pf2-note"><Info size={13} /> Les informations sont attribuées à leurs sources. Les annonces et résultats sont distingués.</div>
            </>
          ) : <Empty label="Engagements" />
        )}

        {/* ═══ AUTRES ONGLETS ═══ */}
        {tab === "parcours" && ((p.parcours || []).length ? (
          <div className="pf-sec"><SecHead Icon={Route} title="Parcours" /><div className="pf-tl">{p.parcours.map((e, i) => (
            <div className="pf-tli" key={i}><span className="meta">{e.period}</span><b>{e.text}</b></div>
          ))}</div></div>
        ) : <Empty label="Parcours" />)}
        {tab === "activite" && (
          <>
            <div className="pf-sec" style={{ paddingBottom: 8 }}>
              <SecHead Icon={Activity} title="Activité" sub="Publications et actions publiques centralisées, avec provenance." />
              <div style={{ fontSize: 10.5, fontWeight: 900, color: "#374151" }}>Période</div>
              <div className="pf-filters">{PERIODS.map(([l, d]) => <button key={l} className={`pf-f ${period === d ? "on" : ""}`} onClick={() => setPeriod(d)} data-testid={`pf-period-${l}`}>{l}</button>)}</div>
              <div style={{ fontSize: 10.5, fontWeight: 900, color: "#374151", marginTop: 6 }}>Source</div>
              <div className="pf-filters">{sources.map((s) => <button key={s} className={`pf-f ${srcF === s ? "on" : ""}`} onClick={() => setSrcF(s)}>{s}</button>)}</div>
              <div style={{ fontSize: 10.5, fontWeight: 900, color: "#374151", marginTop: 6 }}>Type d'activité</div>
              <div className="pf-filters">{types.map((t) => <button key={t} className={`pf-f ${typeF === t ? "on" : ""}`} onClick={() => setTypeF(t)} data-testid={`pf-type-${t}`}>{t}</button>)}</div>
            </div>
            {acts.length === 0 ? <Empty label="Activité (filtres actuels)" /> : (
              <div className="pf-sec"><div className="pf-tl" data-testid="pf-activity-timeline">
                {acts.map((a, i) => (
                  <div className="pf-tli" key={i} data-testid={`pf-activity-${i}`}>
                    <span className="meta">{dateFr(a.date)} · {a.type.toUpperCase()}</span>
                    <b>{a.title}</b>
                    <p>{a.summary}</p>
                    <span className="pf-src">🔗 {a.source} {a.url ? <a href={a.url} target="_blank" rel="noreferrer" style={{ color: "#0E8A4F" }}><ExternalLink size={10} /></a> : "· lien source non disponible"}</span>
                  </div>
                ))}
              </div></div>
            )}
            <div className="pf-sec" style={{ fontSize: 11.5, color: "#6B7280" }}>Sources connectées (LinkedIn, Facebook, presse) : aucune intégration temps réel configurée pour l'instant — les éléments affichés proviennent du registre éditorial AYA.</div>
          </>
        )}
        {tab === "fonctions" && ((p.fonctions || []).length ? (
          <div className="pf-sec"><SecHead Icon={Landmark} title="Fonctions" />{p.fonctions.map((f, i) => (
            <div key={i} style={{ padding: "9px 0", borderBottom: "1px solid #F3F4F6" }}><b style={{ fontSize: 13.5 }}>{f.label}</b><div style={{ fontSize: 12, color: "#6B7280" }}>{f.org}{f.since ? ` · depuis ${f.since}` : ""}</div></div>
          ))}</div>
        ) : <Empty label="Fonctions" />)}
        {tab === "oeuvres" && ((p.oeuvres || []).length || (p.music || []).length ? (
          <div className="pf-sec"><SecHead Icon={BookOpen} title={oeuvresLabel} />
            {(p.oeuvres || []).map((o, i) => <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, fontWeight: 800 }}>{o.label}</div>)}
            {(p.music || []).map((m, i) => <div key={`m${i}`} style={{ padding: "8px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, fontWeight: 800 }}>🎵 {m.title}</div>)}
          </div>
        ) : <Empty label={oeuvresLabel} />)}
        {tab === "photos" && ((p.photos || []).length ? (
          <div className="pf-sec"><SecHead Icon={Camera} title="Photos" sub="Visuels d'illustration" /><div className="pf-grid2">{p.photos.map((ph) => <img key={ph} src={ph} alt="" />)}</div></div>
        ) : <Empty label="Photos" />)}
        {tab === "videos" && ((p.videos || []).length ? (
          <div className="pf-sec"><SecHead Icon={PlayCircle} title="Vidéos" sub="Aperçus de démonstration" />
            <div className="pf2-vidgrid">
              {p.videos.map((v, i) => (
                <div className="pf2-vid w" key={i} onClick={() => demo("Lecture vidéo")}>
                  <div className="th"><img src={v.image} alt="" /><span className="pl"><PlayCircle size={24} color="#fff" /></span>{v.duration && <span className="dur">{v.duration}</span>}</div>
                  <b>{v.title}</b>
                </div>
              ))}
            </div>
          </div>
        ) : <Empty label="Vidéos" />)}
      </div>

      {/* Barre d'actions fixe (maquettes) */}
      <div className="pf2-actions" data-testid="pf-action-bar">
        <button className="pf2-act ghost" data-testid="pf-contact-btn" onClick={() => demo("Contacter")}><MessageCircle size={15} /> Contacter</button>
        <button className="pf2-act ghost" data-testid="pf-rdv-btn" onClick={() => demo("Prendre rendez-vous")}><CalendarDays size={15} /> Prendre rendez-vous</button>
        <button className="pf2-act main" data-testid="pf-follow-btn" onClick={() => toast.success(`Vous suivez ${p.short_name}.`)}><Star size={15} /> Suivre cette personnalité</button>
      </div>
    </div>
  );
}
